import {
  AccordionItem,
  AccordionItemContent,
  AccordionItemTrigger,
} from '@/components/ui/accordion';
import {
  Box,
  Button,
  Card,
  HStack,
  Icon as IconWrapper,
  IconButton,
  Menu,
  Portal,
  Text,
  useBreakpointValue,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import { ReactElement } from 'react';
import { MoreHorizontal } from 'react-feather';

export interface NavbarItemProps {
  text: string;
  tooltip: string;
  icon: any;
  isActive: boolean;
  isOpen: boolean;
  isDisabled?: boolean;
  tabIndex: number;
  onClick: () => void;
}

export function NavbarItem({ ...props }: NavbarItemProps) {
  const text = useBreakpointValue({ base: props.text, lg: props.text });

  return (
    <Card.Root
      variant="elevated"
      width="100%"
      m={0}
      background={
        props.isDisabled ? 'grey' : props.isActive ? 'navButtonActive' : 'navButtonDefault'
      }
    >
      <Card.Body
        width="100%"
        p={2}
        minHeight={10}
        maxHeight={10}
        borderRadius="0.375rem"
        _hover={{
          backgroundColor: props.isDisabled ? 'grey' : 'navButtonHover',
          cursor: 'pointer',
        }}
        tabIndex={props.tabIndex}
        onClick={props.onClick}
      >
        <HStack
          alignItems="center"
          width={props.isOpen ? 'full' : 'auto'}
          justifyContent={props.isOpen ? 'flex-start' : 'center'}
        >
          <Tooltip content={props.tooltip} openDelay={1000} positioning={{ placement: 'right' }}>
            <HStack width="full" justifyContent={props.isOpen ? 'flex-start' : 'center'}>
              <IconWrapper as={props.icon} color="whiteIcon" height={6} width={6} />

              <Text
                fontSize="medium"
                fontWeight={500}
                textAlign={props.isOpen ? 'left' : 'center'}
                alignContent="center"
                transition="hideBelow 1s"
                color="whiteIcon"
                display={props.isOpen ? 'block' : 'none'}
              >
                {text}
              </Text>
            </HStack>
          </Tooltip>
        </HStack>
      </Card.Body>
    </Card.Root>
  );
}

interface NavbarItemAccordionProps extends NavbarItemProps {
  text: string;
  children: any[];
  dynamicSize?: boolean;
}

export function NavbarItemAccordion({ ...props }: NavbarItemAccordionProps) {
  return (
    <AccordionItem
      key={props.text}
      value={props.text}
      width="100%"
      height="auto"
      display="flex"
      flexDirection="column"
      padding={0}
      margin={0}
    >
      <HStack width="100%" minHeight={10} maxHeight={10} p={0} gap={0}>
        <Tooltip content={props.tooltip} openDelay={1000} positioning={{ placement: 'right' }}>
          <HStack
            borderRadius="0.375rem"
            width="100%"
            p={2}
            justifyContent={props.isOpen ? 'flex-start' : 'center'}
            background={
              props.isDisabled ? 'grey' : props.isActive ? 'navButtonActive' : 'navButtonDefault'
            }
            _hover={{
              backgroundColor: props.isDisabled ? 'grey' : 'navButtonHover',
              cursor: 'pointer',
            }}
            tabIndex={props.tabIndex}
            onClick={props.onClick}
          >
            <IconWrapper as={props.icon} color="whiteIcon" height={6} width={6} />

            <Text
              fontSize="medium"
              fontWeight={500}
              textAlign={props.isOpen ? 'left' : 'center'}
              alignContent="center"
              transition="hideBelow 1s"
              color="whiteIcon"
              display={props.isOpen ? 'block' : 'none'}
            >
              {props.text}
            </Text>
          </HStack>
        </Tooltip>

        <AccordionItemTrigger
          height="100%"
          p={2}
          color="whiteIcon"
          _hover={{ backgroundColor: 'navButtonHover' }}
          tabIndex={props.tabIndex + 5}
          display={props.isOpen ? 'block' : 'none'}
          width={props.isOpen ? 'auto' : '0%'}
        />
      </HStack>

      <AccordionItemContent
        p={0}
        borderRadius="6"
        // overflow="scroll"
        overflow="hidden"
        height={props.isOpen ? '100%' : '0%'}
        background="transparent"
      >
        <VStack
          background="none"
          gap={0}
          overflow="hidden"
          overflowY="auto"
          display={props.isOpen ? 'block' : 'none'}
          maxHeight={100}
          scrollbarWidth="sm"
          height={props.isOpen ? 100 : '0%'}
        >
          {props.children !== null && props.children.length !== 0 ? (
            props.children
          ) : (
            <Text p={2} pl={6} fontSize="md" color="whiteIcon">
              Nothing here yet
            </Text>
          )}
        </VStack>
      </AccordionItemContent>
    </AccordionItem>
  );
}

export type NavbarAccordionChildOptions = {
  text: string;
  onClick: () => void;
  icon: ReactElement;
};

interface NavbarAccordionChildProps extends NavbarItemProps {
  options?: NavbarAccordionChildOptions[];
  viewId: string;
}

export function NavbarAccordionChild({ ...props }: NavbarAccordionChildProps) {
  const menuDisclosure = useDisclosure();

  return (
    <HStack
      height="100%"
      width="full"
      alignItems="center"
      justifyContent={'space-between'}
      gap={0}
      minHeight={10}
      maxHeight={10}
    >
      <Box width="100%">
        <Button
          variant="ghost"
          width="100%"
          background={
            props.isDisabled ? 'grey' : props.isActive ? 'navButtonActive' : 'navButtonDefault'
          }
          tabIndex={props.tabIndex}
          onClick={props.onClick}
          _hover={{
            bg: 'navButtonHover',
          }}
        >
          <IconWrapper as={props.icon} color="whiteIcon" height={6} width={6} />

          <HStack
            width="full"
            alignItems="center"
            justifyContent={props.isOpen ? 'flex-start' : 'center'}
          >
            <Tooltip content={props.text} openDelay={1000} positioning={{ placement: 'right' }}>
              <Text
                fontSize="small"
                textAlign={props.isOpen ? 'left' : 'center'}
                alignContent="center"
                transition="hideBelow 1s"
                width={160}
                fontWeight={500}
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                color="whiteIcon"
                display={props.isOpen ? 'block' : 'none'}
              >
                {props.text}
              </Text>
            </Tooltip>
          </HStack>
        </Button>
      </Box>

      <Menu.Root p={0} m={0} positioning={{ placement: 'right-start' }} height="100%">
        <Menu.Trigger
          background="navButtonDefault"
          p={0}
          m={0}
          height="100%"
          width={props.options && props.options.length > 0 ? 'auto' : '0%'}
          display={props.options && props.options.length > 0 ? 'block' : 'none'}
        >
          <IconButton
            size="sm"
            height="100%"
            aria-label="sidebar_button_options"
            as={'div'}
            aria-hidden={false}
            tabIndex={props.tabIndex + 5}
            variant="ghost"
            _hover={{ bg: 'navButtonHover' }}
            onClick={() => {
              menuDisclosure.onOpen();
            }}
          >
            <IconWrapper as={MoreHorizontal} color="whiteIcon" />
          </IconButton>
        </Menu.Trigger>

        <Portal>
          <Menu.Positioner position="right">
            <Menu.Content backgroundColor="cardDefault">
              {props.options ? (
                props.options.map((option, i) => (
                  <Menu.Item
                    key={`menu_option_${i}`}
                    value={option.text}
                    css={{
                      '&[data-state="checked"]': {
                        background: 'cardActive',
                      },
                    }}
                    color="secondaryText"
                    _focus={{ bg: 'cardDefault' }}
                    _hover={{ bg: 'cardHover', transition: 'background-color 0.2s' }}
                    _active={{ bg: 'cardActive', transition: 'background-color 0.2s' }}
                    onClick={() => {
                      option.onClick();
                    }}
                  >
                    {option.icon}
                    {option.text}
                  </Menu.Item>
                ))
              ) : (
                <></>
              )}
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>
    </HStack>
  );
}
